import React from 'react';
import moment from 'moment';
import { FileText } from '@phosphor-icons/react';

import { PromiseToPayWithUser } from '@dill/dill-shared';
import { DillModal, DillButton } from '../../../../general/Dill';

interface PromiseToPayDetailsModalProps {
  open: boolean;
  promise: PromiseToPayWithUser | null;
  onClose: () => void;
  onCancel: (promiseToPayId: string) => void;
}

const sourceLabels: Record<string, string> = {
  PHONE: 'Phone',
  EMAIL: 'Email',
  TEXT: 'Text',
  IN_PERSON: 'In-Person',
};

const typeLabels: Record<string, string> = {
  FULL_PAYMENT: 'Full Payment',
  PARTIAL_PAYMENT: 'Partial Payment',
  PAYMENT_PLAN: 'Payment Plan',
};

const reasonLabels: Record<string, string> = {
  WAITING_ON_GC_PAYMENT: 'Waiting on GC Payment',
  CASH_FLOW_SHORTAGE: 'Cash Flow Shortage',
  MISSING_INVOICE_COPY_OR_STATEMENT: 'Missing Invoice Copy or Statement',
  NEEDS_LIEN_WAIVER: 'Needs Lien Waiver',
  RESOLVED_DISPUTE: 'Resolved Dispute',
  OTHER: 'Other',
};

const statusStyles: Record<string, string> = {
  ACTIVE: 'bg-blue-50 text-blue-700 border border-blue-200',
  FULFILLED: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  BROKEN: 'bg-red-50 text-red-700 border border-red-200',
  CANCELLED: 'bg-gray-50 text-gray-600 border border-gray-200',
};

const DetailRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-[11px] uppercase tracking-wide text-gray-400 font-medium">{label}</span>
    <div className="text-sm text-gray-900">{children}</div>
  </div>
);

export const PromiseToPayDetailsModal = ({
  open,
  promise,
  onClose,
  onCancel,
}: PromiseToPayDetailsModalProps) => {
  if (!promise) return null;

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title="Promise to Pay Details"
      maxWidth="480px"
      footer={
        <div className="flex gap-2">
          <DillButton label="Close" onClick={onClose} variant="secondary" />
          {promise.status === 'ACTIVE' && (
            <DillButton
              label="Cancel Promise"
              onClick={() => {
                onCancel(promise.id);
                onClose();
              }}
              variant="danger"
            />
          )}
        </div>
      }>
      {/* Status */}
      <DetailRow label="Status">
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            statusStyles[promise.status] || statusStyles.CANCELLED
          }`}>
          {promise.status}
        </span>
      </DetailRow>

      {/* Source */}
      <DetailRow label="Source">
        {promise.source ? sourceLabels[promise.source] || promise.source : '-'}
      </DetailRow>

      {/* Type */}
      <DetailRow label="Type">
        {promise.type ? typeLabels[promise.type] || promise.type : '-'}
      </DetailRow>

      {/* Reason */}
      <DetailRow label="Reason">
        {promise.reason
          ? promise.reason === 'OTHER' && promise.reasonOther
            ? `Other — ${promise.reasonOther}`
            : reasonLabels[promise.reason] || promise.reason
          : '-'}
      </DetailRow>

      {/* Promised Payment Date */}
      <DetailRow label="Promised Date">
        {promise.promisedPaymentDate
          ? moment(promise.promisedPaymentDate).format('MM/DD/YYYY')
          : '-'}
      </DetailRow>

      {/* Amount */}
      <DetailRow label="Amount">
        {promise.amount != null ? (
          <span className="font-medium tabular-nums">${promise.amount.toLocaleString()}</span>
        ) : (
          '-'
        )}
      </DetailRow>

      {/* Created By */}
      <DetailRow label="Created By">
        {promise.createdByUser
          ? `${promise.createdByUser.firstName || ''} ${
              promise.createdByUser.lastName || ''
            }`.trim() ||
            promise.createdByUser.email ||
            '-'
          : '-'}
      </DetailRow>

      {/* Created Date */}
      <DetailRow label="Created">
        {promise.createdAt ? moment(promise.createdAt).format('MM/DD/YYYY') : '-'}
      </DetailRow>

      {/* Notes */}
      {promise.notes && (
        <div className="py-2">
          <span className="text-[11px] uppercase tracking-wide text-gray-400 font-medium block mb-1">
            Notes
          </span>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{promise.notes}</p>
        </div>
      )}

      {/* References */}
      {promise.references && promise.references.length > 0 && (
        <div className="py-2">
          <span className="text-[11px] uppercase tracking-wide text-gray-400 font-medium block mb-2">
            Invoices
          </span>
          <div className="flex flex-wrap gap-1">
            {promise.references.map((ref, idx) => (
              <span
                key={`${ref.type}-${ref.id}-${idx}`}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  ref.type === 'INVOICE'
                    ? 'bg-purple-50 text-purple-700 border border-purple-200'
                    : 'bg-blue-50 text-blue-500 border border-blue-200'
                }`}>
                {ref.type === 'INVOICE' ? <FileText size={10} weight="fill" /> : null}
                {ref.description || ref.id}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Broken detected */}
      {promise.status === 'BROKEN' && promise.brokenDetectedAt && (
        <DetailRow label="Broken Detected">
          <span className="text-red-600">
            {moment(promise.brokenDetectedAt).format('MM/DD/YYYY')}
          </span>
        </DetailRow>
      )}

      {/* Cancelled info */}
      {promise.status === 'CANCELLED' && promise.cancelledAt && (
        <DetailRow label="Cancelled">{moment(promise.cancelledAt).format('MM/DD/YYYY')}</DetailRow>
      )}

      {/* Fulfilled info */}
      {promise.status === 'FULFILLED' && promise.fulfilledAt && (
        <DetailRow label="Fulfilled">
          <span className="text-emerald-600">
            {moment(promise.fulfilledAt).format('MM/DD/YYYY')}
          </span>
        </DetailRow>
      )}
    </DillModal>
  );
};
